import { PartnerType } from './partner';

export enum Currency {
  EUR = 'EUR',
  KM = 'KM',
  RSD = 'RSD'
}

export interface CreateTenantRequest {
  primaryPartnerId: number;
  currency: Currency;
}

export interface Tenant {
  id: number;
  active: boolean;
  currency: Currency;
  caseDaysWarn?: number;
  caseDaysUrgent?: number;
  taxNumberPrefix?: string;
  headerAttachment?: {
    id?: string;
    fileName?: string;
    extension?: string;
    url?: string;
  };
  footerAttachment?: {
    id?: string;
    fileName?: string;
    extension?: string;
    url?: string;
  };
}

// Keep in sync with dentallink .../tenant/model/FeatureFlag.java
export enum FeatureFlag {
  FEATURE_COURIER = 'FEATURE_COURIER',
  FEATURE_CHAT_BOT = 'FEATURE_CHAT_BOT',
  FEATURE_PATIENT_PROTOCOL = 'FEATURE_PATIENT_PROTOCOL',
  FEATURE_STOCK_MIN = 'FEATURE_STOCK_MIN',
  FEATURE_WAGES = 'FEATURE_WAGES',
  FEATURE_FINANCE = 'FEATURE_FINANCE',
  FEATURE_API = 'FEATURE_API',
  FEATURE_VOICE_MESSAGES = 'FEATURE_VOICE_MESSAGES',
  FEATURE_USERS = 'FEATURE_USERS',
  FEATURE_ACCOUNTING = 'FEATURE_ACCOUNTING',
  FEATURE_SMS = 'FEATURE_SMS',
  FEATURE_STORAGE = 'FEATURE_STORAGE',
  FEATURE_SUPPORT_EMAIL = 'FEATURE_SUPPORT_EMAIL',
  FEATURE_SUPPORT_CHAT = 'FEATURE_SUPPORT_CHAT',
  FEATURE_STOCK = 'FEATURE_STOCK',
  FEATURE_CATALOG = 'FEATURE_CATALOG',
  FEATURE_PHASES = 'FEATURE_PHASES'
}

export const FEATURE_FLAG_LABELS: Record<FeatureFlag, string> = {
  [FeatureFlag.FEATURE_COURIER]: 'Courier',
  [FeatureFlag.FEATURE_CHAT_BOT]: 'AI Assistant',
  [FeatureFlag.FEATURE_PATIENT_PROTOCOL]: 'Patient Protocol',
  [FeatureFlag.FEATURE_STOCK_MIN]: 'Material Issue Notes',
  [FeatureFlag.FEATURE_WAGES]: 'Wages & Worker Payments',
  [FeatureFlag.FEATURE_FINANCE]: 'Finance & Invoicing',
  [FeatureFlag.FEATURE_API]: 'Public API',
  [FeatureFlag.FEATURE_VOICE_MESSAGES]: 'Voice Messages',
  [FeatureFlag.FEATURE_USERS]: 'User Management',
  [FeatureFlag.FEATURE_ACCOUNTING]: 'Accounting',
  [FeatureFlag.FEATURE_SMS]: 'Shipment SMS',
  [FeatureFlag.FEATURE_STORAGE]: 'File storage',
  [FeatureFlag.FEATURE_SUPPORT_EMAIL]: 'Email support',
  [FeatureFlag.FEATURE_SUPPORT_CHAT]: 'Instant messaging support',
  [FeatureFlag.FEATURE_STOCK]: 'Warehouse (Stocks)',
  [FeatureFlag.FEATURE_CATALOG]: 'Material catalogs',
  [FeatureFlag.FEATURE_PHASES]: 'Case phases'
};

/**
 * A curated label if we have one, otherwise the raw enum name humanised, so a flag the backend
 * grew after this build still reads as words (FEATURE_NEW_THING -> "New Thing").
 */
export function featureFlagLabel(name: string): string {
  return FEATURE_FLAG_LABELS[name as FeatureFlag]
    || name.replace(/^FEATURE_/, '')
           .replace(/_/g, ' ')
           .toLowerCase()
           .replace(/\b\w/g, c => c.toUpperCase());
}

// Keep in sync with dentallink .../tenant/model/QuotaUnit.java
export enum QuotaUnit {
  NONE = 'NONE',
  MINUTES = 'MINUTES',
  REQUESTS = 'REQUESTS',
  SEATS = 'SEATS',
  STOCKS = 'STOCKS',
  MESSAGES = 'MESSAGES',
  GIGABYTES = 'GIGABYTES',
  HOURS = 'HOURS'
}

/**
 * What a metered flag's numbers are called on screen. Empty for a flag that costs nothing.
 *
 * Seats are said without "/month" on purpose: they are a standing capacity, not an allowance that
 * refills - a lab with 25 seats may hold 25 accounts, not add 25 people every month. Stocks are
 * the same kind of number: how many active warehouses a tenant may run at once.
 */
export const QUOTA_UNIT_LABELS: Record<QuotaUnit, string> = {
  [QuotaUnit.NONE]: '',
  [QuotaUnit.MINUTES]: 'min/month',
  [QuotaUnit.REQUESTS]: 'requests/month',
  [QuotaUnit.SEATS]: 'users',
  [QuotaUnit.STOCKS]: 'active stocks',
  [QuotaUnit.MESSAGES]: 'SMS segments/month',
  [QuotaUnit.GIGABYTES]: 'GB total',
  [QuotaUnit.HOURS]: 'hours to respond'
};

export interface FeatureFlagDTO {
  name: FeatureFlag;
  enabled: boolean;
  // NONE for a feature that costs nothing per use and therefore has no allowance.
  unit: QuotaUnit;
  // The tenant's own negotiated figure, or null when it is on the free plan.
  monthlyLimit: number | null;
  // What it actually gets — its own figure, or the free plan's. Null for an unmetered flag.
  effectiveMonthlyLimit: number | null;
  // Whether the feature is released. A feature still in development is not published, and the
  // web app's subscription page does not list it. Global — the same for every tenant.
  published: boolean;
}

export type StorageState = 'OK' | 'WARNING' | 'GRACE' | 'BLOCKED' | 'UNLIMITED';

/** A tenant's storage. The limit is a total, not an allowance per month. */
export interface StorageUsage {
  usedBytes: number;
  limitBytes: number | null;
  state: StorageState;
  blockedFrom: string | null;
}

/** What a tenant can still send, in segments. */
export interface SmsCredit {
  monthlyAllowance: number;
  usedThisMonth: number;
  topUpBalance: number;
  available: number;
}

export type SmsCreditReason = 'TOPUP' | 'ADJUST' | 'SPEND';

export interface SmsCreditLedgerEntry {
  id: number;
  delta: number;
  reason: SmsCreditReason;
  outboxSmsId: number | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface ChangeSmsCreditRequest {
  amount: number;
  reason: 'TOPUP' | 'ADJUST';
  note: string | null;
  // Stays the same across retries of one change, so a double click counts once.
  requestId: string;
}

export interface UpdateFeatureFlagRequest {
  featureFlag: FeatureFlag;
  enabled: boolean;
  // Always sent for a metered flag: null puts the tenant back on the free plan, which is what an
  // emptied input means. Ignored by the backend for anything unmetered.
  monthlyLimit: number | null;
}

// The feature-flags GET endpoint returns { items, totalCount } (backend
// common.api.CollectionDTO), NOT the app's PaginatedCollection { data, size, total }.
export interface FeatureFlagCollection {
  items: FeatureFlagDTO[];
  totalCount: number;
}

/**
 * One row of the hub's feature-flag matrix: a tenant and every flag the backend reported for it.
 * Built client-side from GET /v1/admin/partners?isTenant=true plus one feature-flags call per tenant.
 */
export interface FeatureFlagMatrixRow {
  tenantId: number;
  tenantName: string;
  type?: PartnerType;
  // Keyed by flag name rather than by the enum so a flag the hub does not know yet still lands.
  flags: Record<string, FeatureFlagDTO>;
  // True when this tenant's flags call failed — its cells show an error mark, not "off".
  flagsFailed: boolean;
  enabledCount: number;
}
